import * as React from 'react';
import {
  FormProvider,
  useFormState,
  type FieldValues,
  type SubmitErrorHandler,
  type SubmitHandler,
  type UseFormReturn,
} from 'react-hook-form';
import { cn } from '@noor/lib/cn';

export interface FormProps<
  TFieldValues extends FieldValues = FieldValues,
  TContext = unknown,
  TTransformedValues = TFieldValues,
  // The native `onInvalid` is shadowed by RHF's error handler — it could never
  // fire anyway, since `noValidate` disables native validation.
> extends Omit<React.ComponentProps<'form'>, 'onSubmit' | 'onInvalid'> {
  /** The RHF instance, typically from `useZodForm`. */
  form: UseFormReturn<TFieldValues, TContext, TTransformedValues>;
  /**
   * Receives the schema-parsed values. May be async — RHF tracks it via
   * `formState.isSubmitting`.
   */
  onSubmit: SubmitHandler<TTransformedValues>;
  /** Called with the field errors when submit-time validation fails. */
  onInvalid?: SubmitErrorHandler<TFieldValues>;
}

/**
 * Form root: provides the RHF context (so `FormField` children auto-wire) and
 * renders the `<form>` with validation-aware submit handling. `noValidate`
 * keeps native browser validation out of the way — Zod owns validation.
 */
export function FormRoot<
  TFieldValues extends FieldValues = FieldValues,
  TContext = unknown,
  TTransformedValues = TFieldValues,
>({
  form,
  onSubmit,
  onInvalid,
  children,
  autoComplete = 'off',
  ...props
}: FormProps<TFieldValues, TContext, TTransformedValues>) {
  return (
    <FormProvider {...form}>
      <form
        data-slot="form"
        noValidate
        autoComplete={autoComplete}
        onSubmit={(event) => void form.handleSubmit(onSubmit, onInvalid)(event)}
        {...props}
      >
        {children}
      </form>
    </FormProvider>
  );
}

/**
 * Form-level (non-field) error — e.g. a failed mutation surfaced via
 * `form.setError('root', { message })`. Renders nothing while there is none.
 */
export function FormRootError({ className, ...props }: React.ComponentProps<'p'>) {
  const { errors } = useFormState();
  const message = errors.root?.message;
  if (!message) return null;
  return (
    <p
      role="alert"
      data-slot="form-root-error"
      className={cn('text-sm text-destructive', className)}
      {...props}
    >
      {message}
    </p>
  );
}
